import { Input, Modal, Table } from "antd";
import {
  fetchVolComparisonMinAccumulatedQty,
  saveVolComparisonMinAccumulatedQty,
} from "api/volSurfaceApi";
import PreLoadPagesSpin from "components/PreLoadPagesSpin";
import { toast } from "helpers/generalHelper";
import {
  isValidMinAccumulatedQtyText,
  parseMinAccumulatedQty,
  sanitizeMinAccumulatedQtyInput,
} from "helpers/VolSurface/volComparisonShortcuts";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CancelButton,
  PrimaryButton,
} from "../CustomButtons/CustomButtons";

const MODAL_Z_INDEX = 2200;

export default function VolComparisonMinAccumulatedQtyModal({
  open,
  dark_Theme,
  productContractStyleId,
  title,
  onClose,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [quantityUnit, setQuantityUnit] = useState("");
  const [tenors, setTenors] = useState([]);
  const [qtyByTenorId, setQtyByTenorId] = useState({});

  const applyPayload = (data) => {
    const rows = Array.isArray(data?.tenors) ? data.tenors : [];
    setQuantityUnit(data?.quantity_unit || "");
    setTenors(rows);
    const next = {};
    rows.forEach((row) => {
      const id = row.style_period_tenor_id;
      const raw = row.min_accumulated_qty;
      next[id] =
        raw === null || raw === undefined || raw === ""
          ? "0"
          : String(raw);
    });
    setQtyByTenorId(next);
  };

  const loadTenors = useCallback(() => {
    if (productContractStyleId == null) return;
    setIsLoading(true);
    fetchVolComparisonMinAccumulatedQty(productContractStyleId)
      .then((res) => {
        if (res?.status === 200) {
          applyPayload(res?.data?.data);
          return;
        }
        toast(res?.data?.message || "Failed to load minimums", "warning");
      })
      .catch(() => {
        // Axios interceptor already toasts non-special errors.
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [productContractStyleId]);

  useEffect(() => {
    if (!open) {
      setTenors([]);
      setQtyByTenorId({});
      setQuantityUnit("");
      return;
    }
    loadTenors();
  }, [open, loadTenors]);

  const qtyColumnTitle = quantityUnit
    ? `Min Accumulated Qty (${quantityUnit})`
    : "Min Accumulated Qty";

  const hasInvalidQty = useMemo(
    () =>
      tenors.some(
        (row) =>
          !isValidMinAccumulatedQtyText(
            qtyByTenorId[row.style_period_tenor_id]
          )
      ),
    [qtyByTenorId, tenors]
  );

  const handleQtyChange = (stylePeriodTenorId, raw) => {
    const sanitized = sanitizeMinAccumulatedQtyInput(raw);
    setQtyByTenorId((prev) => ({
      ...prev,
      [stylePeriodTenorId]: sanitized,
    }));
  };

  const handleSave = () => {
    if (hasInvalidQty) {
      toast("Enter a non-negative number for every tenor.", "warning");
      return;
    }

    setIsSaving(true);
    saveVolComparisonMinAccumulatedQty({
      product_contract_style_id: productContractStyleId,
      tenors: tenors.map((row) => ({
        style_period_tenor_id: row.style_period_tenor_id,
        min_accumulated_qty: parseMinAccumulatedQty(
          qtyByTenorId[row.style_period_tenor_id]
        ),
      })),
    })
      .then((res) => {
        if (res?.status === 200) {
          applyPayload(res?.data?.data);
          toast(res?.data?.message || "Minimums saved.", "success");
          onClose?.();
          return;
        }
        toast(res?.data?.message || "Failed to save minimums", "warning");
      })
      .catch(() => {
        // 422: tenor list drifted — reload GET and show the new rows.
        loadTenors();
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  return (
    <Modal
      open={open}
      title={title || "Min Accumulated Qty"}
      onCancel={onClose}
      centered
      destroyOnClose
      width={560}
      zIndex={MODAL_Z_INDEX}
      className={`${dark_Theme ? "ant-modal-dark content_dark" : ""}`}
      footer={[
        <CancelButton key="cancel" onClick={onClose} disabled={isSaving}>
          Cancel
        </CancelButton>,
        <PrimaryButton
          key="save"
          loading={isSaving}
          disabled={isLoading || isSaving || tenors.length === 0 || hasInvalidQty}
          onClick={handleSave}
        >
          Save
        </PrimaryButton>,
      ]}
    >
      {isLoading ? (
        <PreLoadPagesSpin
          className="other-info--container--spin"
          size="large"
          dark_Theme={dark_Theme}
        />
      ) : (
        <Table
          pagination={false}
          bordered
          size="small"
          className={dark_Theme ? "table-dark" : ""}
          rowKey={(record) => record.style_period_tenor_id}
          dataSource={tenors}
          scroll={{ y: 360 }}
          columns={[
            {
              title: "Tenor",
              dataIndex: "tenor",
              key: "tenor",
              width: "40%",
            },
            {
              title: qtyColumnTitle,
              dataIndex: "style_period_tenor_id",
              key: "min_accumulated_qty",
              render: (id) => (
                <Input
                  value={qtyByTenorId[id] ?? ""}
                  inputMode="decimal"
                  onChange={(e) => handleQtyChange(id, e.target.value)}
                  status={
                    isValidMinAccumulatedQtyText(qtyByTenorId[id])
                      ? undefined
                      : "error"
                  }
                />
              ),
            },
          ]}
        />
      )}
    </Modal>
  );
}
